import { ref } from 'vue'
import { useFeatures } from '@/composables/useFeatures'
import { ApiError, api } from '@/utils/apiClient'

// Operatøravtale gate (#3025 P1). The agreement covers the commercial terms
// for Stemne payments, so it is requested when an operator enables payments,
// not as a prerequisite for ordinary onboarding or dashboard use.
//
// Exact mirror of useDpaGate, deliberately a SECOND composable rather than a
// widening of the first: the two agreements carry independent version stamps
// server-side, so an operator can owe one and not the other. One cache that
// merged them could not represent that.
//
// This composable is the SPA half of the gate: it reads the org's acceptance
// status and renders the payment-specific accept screen when the backend says
// payment activation requires it. The load-bearing enforcement is the backend
// RequireTermsAccepted middleware.
//
// Module-scoped cache, best-effort load, FAIL-OPEN when the status can't be
// resolved (the backend gate is the backstop). Invalidated after accept.

// The two agreements share ONE blocking screen, which still lives at the
// path the DPA gate introduced. Renaming the route to something that covers
// both documents is a follow-up, not part of this change — the constant is
// here so the rename is one edit per composable.
export const TERMS_ACCEPT_PATH = '/dpa-accept'

// party is the operator's own business block, auto-filled server-side from
// the org row (name, orgnr) plus the signed-in operator's email. Optional so
// a response where the org row couldn't be resolved degrades to no block
// rather than rendering "undefined" into a contract.
export type TermsParty = {
  name: string
  orgnr: string
  email: string
}

export type TermsGateStatus = {
  current_version: string
  accepted_version: string | null
  accepted_at: string | null
  unlocked: boolean
  party?: TermsParty | null
}

const status = ref<TermsGateStatus | null>(null)
const loaded = ref(false)
const loading = ref(false)

// shouldBounceToTermsAccept is retained as a no-op compatibility seam for the
// router. Operator terms are enforced by payment activation routes, so no
// tenant-app navigation should redirect here.
export function shouldBounceToTermsAccept(_path: string, _active: boolean): string | null {
  // The operator agreement is payment-specific. It must not block the
  // dashboard or ordinary booking setup; payment activation routes enforce
  // it server-side and send the owner to the agreement when needed.
  return null
}

// invalidateTermsStatus drops the cache so the next loadTermsStatus re-hits
// the API — called after a successful accept so the guard sees the fresh
// (unlocked) state on the next navigation instead of bouncing off stale
// state. Mirrors invalidateDpaStatus.
export function invalidateTermsStatus(): void {
  status.value = null
  loaded.value = false
}

export function useTermsGate() {
  async function loadTermsStatus(): Promise<void> {
    if (loaded.value || loading.value) return
    loading.value = true
    try {
      status.value = await api.get<TermsGateStatus>('/api/admin/terms/gate')
      loaded.value = true
    } catch (e) {
      // Best-effort. When operator.terms-accept is off the route 404s — leave
      // status null so gateActive() is false and the guard fails open. A
      // 401/403/404 is terminal for this tab (no retry-storm); transient
      // failures stay retryable so a blip doesn't strand the operator.
      status.value = null
      if (e instanceof ApiError && [401, 403, 404].includes(e.status)) {
        loaded.value = true
      }
    } finally {
      loading.value = false
    }
  }

  async function accept(): Promise<TermsGateStatus | null> {
    if (!status.value) return null
    const next = await api.post<TermsGateStatus>('/api/admin/terms/gate/accept', {
      version: status.value.current_version,
    })
    status.value = next
    loaded.value = true
    return next
  }

  // gateActive is the raw boolean the router guard branches on. Non-reactive
  // (uses peek) because guards run outside a reactive scope. True only when
  // the flag is on, the status has loaded, and it isn't unlocked — so an
  // unresolved status (cold nav) fails open, and a flag-off org is never
  // gated.
  //
  // peek, not flagRef: a route guard wants a real boolean. flagRef returns a
  // ComputedRef, and a ref object is always truthy (see the hard rule in
  // CLAUDE.md and frontend/src/__tests__/featureFlagGates.spec.ts).
  function gateActive(): boolean {
    return (
      useFeatures().peek('operator.terms-accept') &&
      loaded.value &&
      status.value !== null &&
      !status.value.unlocked
    )
  }

  return { status, loaded, loading, loadTermsStatus, accept, gateActive }
}
